import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  fetchSupportNotificationSettings,
  updateSupportNotificationSettings,
} from '../api/authApi';
import { colorTokens, radiusTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { ErrorState } from '../components/ErrorState';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';

const { semantic } = colorTokens;

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export function SupportNotificationSettingsPage() {
  const { t } = useTranslation(['support', 'common']);
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.support.notification_settings.manage');
  const canView = hasPermission(session, 'system.support.view') || canManage;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState(null);
  const [draftRecipients, setDraftRecipients] = useState([]);
  const [draftEnabled, setDraftEnabled] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [fieldError, setFieldError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSupportNotificationSettings();
      setSettings(data);
      setDraftRecipients(Array.isArray(data.recipients) ? data.recipients : []);
      setDraftEnabled(data.enabled !== false);
    } catch (err) {
      setError(err?.response?.data?.message || t('notification_settings.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canView) {
      load();
    } else {
      setLoading(false);
    }
  }, [canView, load]);

  const lastChangedLabel = useMemo(() => {
    if (!settings?.updated_by && !settings?.updated_at) {
      return t('notification_settings.never_saved');
    }
    const who = settings?.updated_by?.name || t('common:empty_value');
    const when = settings?.updated_at ? new Date(settings.updated_at).toLocaleString() : '';
    return t('notification_settings.last_changed', { who, when });
  }, [settings, t]);

  const addEmail = () => {
    setFieldError('');
    const email = String(newEmail || '').trim().toLowerCase();
    if (!email) {
      return;
    }
    if (!isValidEmail(email)) {
      setFieldError(t('notification_settings.invalid_email'));
      return;
    }
    if (draftRecipients.includes(email)) {
      setFieldError(t('notification_settings.duplicate_email'));
      return;
    }
    setDraftRecipients((prev) => [...prev, email]);
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setDraftRecipients((prev) => prev.filter((item) => item !== email));
  };

  const save = async () => {
    if (!canManage || !settings) {
      return;
    }
    setSaving(true);
    setSuccess('');
    setError(null);
    setFieldError('');
    try {
      const data = await updateSupportNotificationSettings({
        recipients: draftRecipients,
        enabled: draftEnabled,
        version: settings.version,
      });
      setSettings(data);
      setDraftRecipients(Array.isArray(data.recipients) ? data.recipients : []);
      setDraftEnabled(data.enabled !== false);
      setSuccess(t('notification_settings.saved'));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setError(t('notification_settings.conflict'));
        await load();
      } else if (status === 422) {
        setFieldError(err?.response?.data?.message || t('notification_settings.invalid_email'));
      } else {
        setError(err?.response?.data?.message || t('notification_settings.save_failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return <ErrorState title={t('notification_settings.forbidden')} description={t('notification_settings.read_only')} />;
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box>
      <PageHeader
        title={t('notification_settings.title')}
        subtitle={t('notification_settings.subtitle')}
      />

      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <SectionCard title={t('notification_settings.recipients_card')}>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ borderRadius: `${radiusTokens.card}px` }}>
              {t('notification_settings.websupport_note')}
            </Alert>
            <Typography variant="body2" color={semantic.text.secondary}>
              {t('notification_settings.proposed_mailbox')}
            </Typography>
            <Typography variant="body2">{lastChangedLabel}</Typography>
            <Typography variant="body2" color={semantic.text.secondary}>
              {t('notification_settings.effective_source', {
                source: settings?.effective_source || 'none',
              })}
            </Typography>
            {settings?.configuration_required ? (
              <Alert severity="warning">{t('notification_settings.configuration_required')}</Alert>
            ) : null}

            <FormControlLabel
              control={
                <Switch
                  checked={draftEnabled}
                  disabled={!canManage || saving}
                  onChange={(e) => setDraftEnabled(e.target.checked)}
                />
              }
              label={t('notification_settings.enabled')}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
              <TextInput
                label={t('notification_settings.add_email')}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={!canManage || saving}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEmail();
                  }
                }}
                sx={{ flex: 1 }}
              />
              <Button variant="secondary" onClick={addEmail} disabled={!canManage || saving}>
                {t('notification_settings.add')}
              </Button>
            </Stack>
            {fieldError ? (
              <Typography variant="body2" color={semantic.error}>
                {fieldError}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {draftRecipients.length === 0 ? (
                <Typography variant="body2" color={semantic.text.secondary}>
                  {t('notification_settings.empty_list')}
                </Typography>
              ) : (
                draftRecipients.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={canManage && !saving ? () => removeEmail(email) : undefined}
                    sx={{ bgcolor: semantic.bg.elevated }}
                  />
                ))
              )}
            </Stack>

            {canManage ? (
              <Box>
                <Button variant="primary" onClick={save} disabled={saving}>
                  {saving ? t('common:saving') : t('notification_settings.save')}
                </Button>
              </Box>
            ) : (
              <Alert severity="info">{t('notification_settings.read_only')}</Alert>
            )}
          </Stack>
        </SectionCard>

        <SectionCard title={t('notification_settings.audit_card')}>
          <Stack spacing={1}>
            {(settings?.audits || []).length === 0 ? (
              <Typography variant="body2" color={semantic.text.secondary}>
                {t('notification_settings.no_audits')}
              </Typography>
            ) : (
              (settings.audits || []).map((audit) => (
                <Box
                  key={audit.id}
                  sx={{
                    p: 1.5,
                    borderRadius: `${radiusTokens.card}px`,
                    border: `1px solid ${semantic.border.subtle}`,
                    bgcolor: semantic.bg.surface,
                  }}
                >
                  <Typography variant="body2">
                    {t('notification_settings.audit_row', {
                      who: audit.changed_by?.name || t('common:empty_value'),
                      when: audit.changed_at ? new Date(audit.changed_at).toLocaleString() : '',
                      from: audit.from_version,
                      to: audit.to_version,
                    })}
                  </Typography>
                  <Typography variant="caption" color={semantic.text.secondary}>
                    {(audit.new_recipients || []).join(', ') || t('notification_settings.empty_list')}
                  </Typography>
                </Box>
              ))
            )}
          </Stack>
        </SectionCard>
      </Stack>
    </Box>
  );
}
